import React, { useEffect, useState } from 'react';
import { Crown, Sparkles, BookOpen, MessageCircle, Send, Instagram, Facebook, Music2 } from 'lucide-react';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { useAuth } from '../../features/auth/AuthContext';
import { StatCard } from '../../components/common/StatCard';
import { DASHBOARD } from '../../constants/testIds';
import { fetchPublicSettings } from '../../features/settings/api/settings';
import type { SiteSettings } from '../../types';
import { toEmbedUrl } from '../../utils/video';

export default function MemberDashboard() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  useEffect(() => { fetchPublicSettings().then(setSettings).catch(() => {}); }, []);

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Dashboard">
      <div data-testid={DASHBOARD.memberRoot} className="max-w-6xl">
        <div
          data-testid={DASHBOARD.welcomeCard}
          className="bg-gradient-to-br from-cyan-400/10 to-transparent border border-cyan-400/20 rounded-2xl p-8 mb-6"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">Welcome</p>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">
            Halo, {user?.full_name} 👋
          </h2>
          <p className="text-white/60 mt-2 text-sm whitespace-pre-line">
            {settings?.welcome_note || 'Selamat datang di Cuantrader Community. Fitur lengkap akan segera hadir.'}
          </p>
          {settings?.welcome_video_url && (
            <div data-testid="welcome-video" className="aspect-video w-full max-w-2xl mt-5 rounded-xl border border-white/10 bg-black/40 overflow-hidden">
              <iframe src={toEmbedUrl(settings.welcome_video_url)} title="Welcome" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard icon={Crown} label="VIP Status" testId={DASHBOARD.vipCard} accent>
            <p className="text-2xl font-light text-white capitalize">
              {user?.is_vip ? user?.vip_status : 'Non-VIP'}
            </p>
            <p className="text-sm text-white/50 mt-1">
              {user?.is_vip ? 'Akses VIP aktif' : 'Upgrade untuk akses eksklusif'}
            </p>
          </StatCard>

          <StatCard icon={BookOpen} label="Upcoming Modules" testId={DASHBOARD.modulesCard}>
            <p className="text-2xl font-light text-white">Segera hadir</p>
            <p className="text-sm text-white/50 mt-1">Modul E-Course akan tersedia di fase berikutnya.</p>
          </StatCard>

          <StatCard icon={Sparkles} label="Akun" testId="dashboard-account-card">
            <p className="text-lg font-light text-white break-all">{user?.email}</p>
            <p className="text-sm text-white/50 mt-1 capitalize">Role: {user?.roles?.join(', ')}</p>
          </StatCard>
        </div>

        {(() => {
          const socials = [
            { key: 'social_whatsapp_channel_url', label: 'WhatsApp', icon: MessageCircle, cls: 'border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/10' },
            { key: 'social_telegram_url', label: 'Telegram', icon: Send, cls: 'border-sky-400/30 text-sky-300 hover:bg-sky-400/10' },
            { key: 'social_instagram_url', label: 'Instagram', icon: Instagram, cls: 'border-fuchsia-400/30 text-fuchsia-300 hover:bg-fuchsia-400/10' },
            { key: 'social_facebook_url', label: 'Meta', icon: Facebook, cls: 'border-blue-400/30 text-blue-300 hover:bg-blue-400/10' },
            { key: 'social_tiktok_url', label: 'TikTok', icon: Music2, cls: 'border-white/20 text-white/80 hover:bg-white/10' },
          ].filter((s) => (settings as any)?.[s.key]);
          if (socials.length === 0) return null;
          return (
            <div data-testid="dashboard-channels-card" className="mt-6 bg-white/[0.02] border border-white/10 rounded-2xl p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-1">Channel & Komunitas</p>
              <h3 className="text-lg font-medium text-white mb-4">Gabung channel resmi kami</h3>
              <div className="flex flex-wrap gap-3">
                {socials.map(({ key, label, icon: Icon, cls }) => (
                  <a
                    key={key}
                    href={(settings as any)[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`dashboard-social-${label.toLowerCase()}`}
                    className={`flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl border transition-colors ${cls}`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
                  </a>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </DashboardLayout>
  );
}
